import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader, ChipFilter, StatusBadge, Modal, MetricCard, Empty } from '../components/UI';

const EMPTY = { date: '', ref: '', type: 'Near Miss', severity: 'Low', description: '', location: '', status: 'Open', reportedBy: '', action: '' };

export default function IncidentLog() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState(null);

  const all = data.incidents.filter(i => i.projectId === activeProject?.id);
  const filtered = filter === 'All' ? all : all.filter(i => i.status === filter);

  const nextRef = () => 'INC-' + String(all.length + 1).padStart(3, '0');

  const save = () => { upsert('incidents', { ...form, projectId: activeProject.id }); setForm(null); };

  const openCount   = all.filter(i => i.status === 'Open').length;
  const highCount   = all.filter(i => i.severity === 'High' || i.severity === 'Critical').length;
  const closedCount = all.filter(i => i.status === 'Closed').length;

  return (
    <div className="page-content">
      <PageHeader title="Incident Log" subtitle={`${activeProject?.name}`}>
        {can('canCreate') && (
          <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY, ref: nextRef(), id: null })}>+ New Incident</button>
        )}
      </PageHeader>

      <div className="grid-4 mb-20">
        <MetricCard label="Total Incidents" value={all.length} />
        <MetricCard label="Open" value={openCount} color="var(--red)" sub={openCount > 0 ? 'Action required' : 'All clear'} />
        <MetricCard label="High / Critical" value={highCount} color={highCount > 0 ? 'var(--amber)' : 'var(--green)'} />
        <MetricCard label="Closed" value={closedCount} color="var(--green)" />
      </div>

      <ChipFilter options={['All', 'Open', 'Under Investigation', 'Closed']} value={filter} onChange={setFilter} />

      {filtered.length === 0
        ? <Empty icon="✅" message="No incidents match this filter." />
        : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>Ref</th><th>Date</th><th>Type</th><th>Severity</th><th>Location</th>
                <th>Description</th><th>Reported By</th><th>Status</th><th>Corrective Action</th>
                {can('canEdit') && <th>Actions</th>}
              </tr></thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.id} className="clickable" onClick={() => setForm({ ...i })}>
                    <td className="td-mono">{i.ref}</td>
                    <td>{i.date}</td>
                    <td><span className="tag">{i.type}</span></td>
                    <td><StatusBadge value={i.severity} /></td>
                    <td style={{ maxWidth: 120 }} className="truncate">{i.location}</td>
                    <td style={{ maxWidth: 200 }} className="truncate td-muted">{i.description}</td>
                    <td className="td-muted">{i.reportedBy}</td>
                    <td><StatusBadge value={i.status} /></td>
                    <td style={{ maxWidth: 160 }} className="truncate td-muted">{i.action}</td>
                    {can('canEdit') && (
                      <td onClick={e => e.stopPropagation()}>
                        <button className="btn btn-danger btn-xs" onClick={() => deleteItem('incidents', i.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {form && (
        <Modal title={form.id ? 'Edit Incident' : 'New Incident'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Ref No.</label><input value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} /></div>
            <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group"><label>Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                {['Near Miss','Injury','Property Damage','Environmental','Fire','Security Incident'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Severity</label>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}>
                {['Low','Medium','High','Critical'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Location on Site</label><input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></div>
            <div className="form-group"><label>Reported By</label><input value={form.reportedBy} onChange={e => setForm({ ...form, reportedBy: e.target.value })} /></div>
            <div className="form-group form-full"><label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="form-group form-full"><label>Corrective / Preventive Action</label>
              <textarea rows={2} value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} />
            </div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['Open','Under Investigation','Closed'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save Incident</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}
